import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import {
  DATE_RANGE_PRESETS,
  getPresetRange,
  habitConsistency,
  overallGoalProgress,
  useGetCompletions,
  useGetGoalLinkedTasks,
  useGetInsights,
  useGetUserHabits,
  useGoalRefinements,
  type DateRangePreset,
  type LocalDateRange,
} from '@zendia/frontend-shared'
import { ActionBar } from '../../../components/ActionBar'
import { Alert, AlertVariants } from '../../../components/Alert'
import {
  DateRangeDropdown,
  type DateRangeSelection,
} from '../../../components/Insights/DateRangeDropdown'
import { LifeBalanceCard } from '../../../components/Insights/LifeBalanceCard'
import { ProductivityExplanation } from '../../../components/Insights/ProductivityExplanation'
import { ProductivityTrendCard } from '../../../components/Insights/ProductivityTrendCard'
import { StatsCard } from '../../../components/Insights/StatsCard'
import { TaskHabitCompletionCard } from '../../../components/Insights/TaskHabitCompletionCard'
import { axiosAuthenticatedRequest } from '../../../utilities/axiosClient'
import { useToday } from '../../../hooks/useToday'

const DEFAULT_SELECTION: DateRangePreset = 'monthToDate'
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

// The range lives in the URL so a refresh or Back keeps it. A preset is stored by name, not by
// its dates, so returning to "Today" tomorrow shows tomorrow.
const insightsSearchSchema = z.object({
  range: z
    .enum([...DATE_RANGE_PRESETS, 'custom'])
    .optional()
    .catch(undefined),
  from: daySchema.optional().catch(undefined),
  to: daySchema.optional().catch(undefined),
})

export const Route = createFileRoute('/_authenticated/insights/')({
  component: InsightsPage,
  validateSearch: insightsSearchSchema,
})

const iconPath = (d: string) => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="size-full">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
)

// Outline icons from the design system's Insights screen
const ICONS = {
  calendar: iconPath(
    'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  ),
  sun: iconPath(
    'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  ),
  pencil: iconPath(
    'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  ),
  book: iconPath(
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  ),
  check: iconPath('M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'),
  flame: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="size-full">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
      />
    </svg>
  ),
  sparkle: iconPath(
    'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
  ),
  chart: iconPath(
    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  ),
  repeat: iconPath(
    'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  ),
}

const days = (count: number) => `${count} ${count === 1 ? 'day' : 'days'}`
const goalsCount = (count: number) => `${count} ${count === 1 ? 'goal' : 'goals'}`

function InsightsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  // Re-renders at local midnight, so a preset like "Today" moves on with the day
  const today = useToday()

  const selection = search.range ?? DEFAULT_SELECTION
  const customRange = useMemo<LocalDateRange>(() => {
    const fallback = getPresetRange(DEFAULT_SELECTION)
    const startDate = search.from ?? fallback.startDate
    const endDate = search.to ?? fallback.endDate
    return startDate <= endDate ? { startDate, endDate } : fallback
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` re-derives the fallback
  }, [search.from, search.to, today])
  const dateRange = useMemo<LocalDateRange>(
    () => (selection === 'custom' ? customRange : getPresetRange(selection)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` re-derives the preset
    [selection, customRange, today],
  )

  const {
    data: response,
    isLoading,
    isError,
  } = useGetInsights(dateRange, axiosAuthenticatedRequest)
  const insights = response?.data

  // Goal Progress and Habit Consistency are all-time figures, so none of these follow the range.
  // Both are worked out here, where the goal page and the dashboard work them out too.
  const goals = useGoalRefinements(axiosAuthenticatedRequest)
  const goalTasks = useGetGoalLinkedTasks(axiosAuthenticatedRequest)
  const habits = useGetUserHabits(axiosAuthenticatedRequest)
  const completions = useGetCompletions(axiosAuthenticatedRequest)

  const goalsList = goals.data?.data
  const habitsList = habits.data?.data?.habits
  const completionsList = completions.data?.data?.completions
  const goalsLoaded = !!goalsList && !!goalTasks.data
  const habitsLoaded = !!habitsList && !!completionsList

  const goalProgress = useMemo(
    () => (goalsList && goalTasks.data ? overallGoalProgress(goalsList, goalTasks.data) : null),
    [goalsList, goalTasks.data],
  )
  const consistency = useMemo(
    () =>
      habitsList && completionsList
        ? habitConsistency({ habits: habitsList, completions: completionsList, today })
        : null,
    [habitsList, completionsList, today],
  )
  const isLoadingKeyStatistics =
    isLoading || goals.isLoading || goalTasks.isLoading || habits.isLoading || completions.isLoading

  const changeSearch = (next: z.infer<typeof insightsSearchSchema>) =>
    navigate({ search: next, replace: true })

  const handleSelectionChange = (next: DateRangeSelection) =>
    changeSearch(
      // Custom opens on the range that was showing, so it starts from something sensible
      next === 'custom'
        ? { range: next, from: dateRange.startDate, to: dateRange.endDate }
        : { range: next },
    )

  const completionRate =
    insights && insights.tasksTotal > 0
      ? Math.round((insights.tasksCompleted / insights.tasksTotal) * 100)
      : null

  return (
    <>
      <ActionBar title="Insights" />
      <div className="flex flex-col gap-6 p-4 max-w-6xl w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <p className="text-[var(--color-body-text-secondary)]">
            Your journey toward balanced, meaningful living
          </p>
          <DateRangeDropdown
            selection={selection}
            customRange={customRange}
            onSelectionChange={handleSelectionChange}
            onCustomRangeChange={(range) =>
              changeSearch({ range: 'custom', from: range.startDate, to: range.endDate })
            }
          />
        </div>

        <section>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">Life Balance</h2>
          <LifeBalanceCard dateRange={dateRange} />
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-1 text-[var(--color-heading)]">
            Productivity &amp; Intentional Living
          </h2>
          <div className="mb-4">
            <ProductivityExplanation />
          </div>
          <ProductivityTrendCard dateRange={dateRange} />
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">
            Task &amp; Habit Completion
          </h2>
          <TaskHabitCompletionCard dateRange={dateRange} />
        </section>

        {isError && (
          <Alert type={AlertVariants.error}>
            We couldn't load your insights. Please try again.
          </Alert>
        )}

        <section aria-busy={isLoadingKeyStatistics}>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">Key Statistics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatsCard
              title="Total Days"
              value={insights ? days(insights.totalActiveDays) : '-'}
              subtitle="Using Zendia, all time"
              icon={ICONS.calendar}
            />
            <StatsCard
              title="Days Active"
              value={insights ? days(insights.activeDaysInRange) : '-'}
              subtitle="In this range"
              icon={ICONS.sun}
            />
            <StatsCard
              title="Journaling Days"
              value={insights ? days(insights.journalingDays) : '-'}
              icon={ICONS.pencil}
            />
            <StatsCard
              title="Journal Entries"
              value={insights ? insights.journalEntries : '-'}
              icon={ICONS.book}
            />
            <StatsCard
              title="Tasks Completed"
              value={insights ? `${insights.tasksCompleted} / ${insights.tasksTotal}` : '-'}
              subtitle={completionRate === null ? undefined : `${completionRate}% completion rate`}
              icon={ICONS.check}
            />
            <StatsCard
              title="Goal Progress"
              value={goalProgress ? `${Math.round(goalProgress.percent)}%` : '-'}
              subtitle={
                !goalsLoaded
                  ? undefined
                  : goalProgress
                    ? `Across ${goalsCount(goalProgress.goals)}`
                    : 'No goals with a plan yet'
              }
              icon={ICONS.chart}
            />
            <StatsCard
              title="Habit Consistency"
              value={consistency ? `${Math.round(consistency.percent)}%` : '-'}
              subtitle={
                !habitsLoaded
                  ? undefined
                  : consistency
                    ? 'All habits, all time'
                    : 'No habits due yet'
              }
              icon={ICONS.repeat}
            />
          </div>
        </section>

        <section aria-busy={isLoading}>
          <h2 className="text-lg font-semibold mb-4 text-[var(--color-heading)]">Streaks</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatsCard
              title="Current Streak"
              value={insights ? days(insights.currentStreak) : '-'}
              subtitle="Keep it going!"
              icon={ICONS.flame}
            />
            <StatsCard
              title="Longest Streak"
              value={insights ? days(insights.longestStreak) : '-'}
              subtitle="Personal best"
              icon={ICONS.sparkle}
            />
          </div>
        </section>
      </div>
    </>
  )
}
